<template>
    <v-row class="ma-0">
        <v-col class="pa-0 px-2">

            <v-container v-if="!mobileEnabled" class="pa-6" fluid>
                <v-alert type="info" text class="mb-0">
                    L'app mobile non è attiva per questo cliente.
                </v-alert>
            </v-container>

            <v-container v-else-if="loading" class="text-center py-8" fluid>
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </v-container>

            <v-container v-else-if="loadError" class="pa-6" fluid>
                <v-alert type="error" text class="mb-0">
                    Registro delle entità mobile non caricato.
                    <v-btn text small color="error" class="ml-2" @click="loadRegistry">Riprova</v-btn>
                </v-alert>
            </v-container>

            <v-container v-else-if="!mainEntities.length" class="pa-6" fluid>
                <v-alert type="info" text class="mb-0">
                    Nessuna entità configurabile da mobile.
                </v-alert>
            </v-container>

            <template v-else>
                <!-- Una scheda per entità, come le schede in testa alle altre pagine di Impostazioni -->
                <v-tabs
                    v-model="entityTab"
                    show-arrows
                    :height="32"
                    class="tabs-route pt-0"
                    background-color="transparent"
                    slider-color="primary">
                    <v-tab v-for="entity in mainEntities" :key="'entity-' + entity.key">
                        {{ $trans(entity.label) }}
                    </v-tab>
                </v-tabs>

                <v-tabs-items v-model="entityTab" class="transparent" touchless>
                    <v-tab-item v-for="entity in mainEntities" :key="'entity-item-' + entity.key">

                        <!-- Dentro l'entità: Card | Form | Righe | Azioni, come Campi | Azioni delle pagine dei moduli -->
                        <v-tabs
                            class="tabs-pill mt-2"
                            height="34"
                            background-color="background-1"
                            slider-color="primary">
                            <v-tab
                                v-for="section in sectionsOf(entity)"
                                :key="'section-' + entity.key + '-' + section.id"
                                ripple
                                class="secondary-tab">
                                {{ section.label }}
                            </v-tab>

                            <v-tab-item
                                v-for="section in sectionsOf(entity)"
                                :key="'section-item-' + entity.key + '-' + section.id"
                                class="background-1 pt-3 pb-1">
                                <v-row class="ma-0 pb-offset">
                                    <v-col class="pa-0 pt-2" cols="12">

                                        <entity-mobile-card-configurator
                                            v-if="section.type === 'card'"
                                            :model="entity.key"
                                            :info-url="entity.resource + '-info?card=1'"
                                        ></entity-mobile-card-configurator>

                                        <v-alert v-else-if="section.type === 'card-fixed'" type="info" text dense class="mb-0">
                                            Card fissa, non configurabile.
                                        </v-alert>

                                        <v-card v-else-if="section.type === 'form'" :elevation="1" class="background-2 round-card" width="100%">
                                            <v-card-title class="pa-3 py-1">
                                                <div class="pl-2 pl-xl-3 subtitle-2">
                                                    Configurazione Form {{ $trans(section.entityLabel) }}<template v-if="section.formLabel"> · {{ $trans(section.formLabel) }}</template>
                                                </div>
                                            </v-card-title>
                                            <v-divider></v-divider>
                                            <fields-form-selector
                                                :modelDef="section.entity"
                                                :templateData="section.form"
                                                mobile
                                            ></fields-form-selector>
                                        </v-card>

                                        <actions-selector
                                            v-else-if="section.type === 'actions'"
                                            :model-def="entity.key"
                                            device="mobile"
                                        ></actions-selector>

                                    </v-col>
                                </v-row>
                            </v-tab-item>
                        </v-tabs>

                    </v-tab-item>
                </v-tabs-items>
            </template>

        </v-col>
    </v-row>
</template>

<script>
import FieldsFormSelector from '@/components/common/FieldsFormSelector'
import ActionsSelector from '@/components/common/ActionsSelector'
import EntityMobileCardConfigurator from '@/components/common/EntityMobileCardConfigurator'

export default {
    name: 'settings-mobile',

    components: {
        FieldsFormSelector,
        ActionsSelector,
        EntityMobileCardConfigurator,
    },

    data() {
        return {
            loading: true,
            loadError: false,
            entities: [],
            entityTab: 0,
        }
    },

    computed: {
        mobileEnabled() {
            return !!this.$store.getters.isMobileEnabled
        },

        byKey() {
            const map = {}
            this.entities.forEach(e => { map[e.key] = e })
            return map
        },

        // Entità riga (le righe di intervento, ordine, DDT): si configurano dentro il documento
        rowKeys() {
            const keys = new Set()
            this.entities.forEach(e => (e.forms || []).forEach(f => { if (f.rows) keys.add(f.rows) }))
            return keys
        },

        // Schede in testa: entità non riga con almeno una sezione da mostrare, nell'ordine del registro
        mainEntities() {
            return this.entities.filter(e => !this.rowKeys.has(e.key) && this.sectionsOf(e).length)
        },
    },

    methods: {
        // AppTabs tiene la tab nascosta finché il progress globale non si chiude:
        // questa pagina non passa da AppTabs ma chiude il progress come le altre.
        endGlobalProgress() {
            this.$store.commit('endProgress')
        },

        /**
         * Input:  voce del registro
         * Fa:     sezioni dell'entità: card (configurabile o fissa), un form per ogni form
         *         configurabile, il form dell'entità riga, le azioni
         * Output: [{id, label, type, entity?, form?, entityLabel?, formLabel?}]
         */
        sectionsOf(entity) {
            const sections = []
            if (entity.card === 'configurable') {
                sections.push({ id: 'card', label: 'Card', type: 'card' })
            } else if (entity.card === 'fixed') {
                sections.push({ id: 'card', label: 'Card', type: 'card-fixed' })
            }

            const forms = (entity.forms || []).filter(f => f.configurable)
            forms.forEach(form => {
                sections.push({
                    id: 'form-' + form.key,
                    label: forms.length > 1 ? form.label : 'Form',
                    type: 'form',
                    entity: entity.key,
                    form: form.key,
                    entityLabel: entity.label,
                    formLabel: form.label,
                })
                const row = form.rows ? this.byKey[form.rows] : null
                const rowForm = row && (row.forms || []).find(f => f.configurable)
                if (rowForm) {
                    sections.push({
                        id: 'rows-' + row.key,
                        label: 'Righe',
                        type: 'form',
                        entity: row.key,
                        form: rowForm.key,
                        entityLabel: row.label,
                        formLabel: null,
                    })
                }
            })

            if (entity.actions) {
                sections.push({ id: 'actions', label: 'Azioni', type: 'actions' })
            }
            // Una card fissa da sola non è niente da configurare
            return sections.some(s => s.type !== 'card-fixed') ? sections : []
        },

        loadRegistry() {
            this.loading = true
            this.loadError = false
            return this.axios.get('mobile-registry').then(response => {
                this.entities = (response.data && response.data.entities) || []
                this.loading = false
                this.endGlobalProgress()
            }).catch(() => {
                this.loading = false
                this.loadError = true
                this.endGlobalProgress()
            })
        },
    },

    watch: {
        // Il flag del cliente arriva con i dati utente: su un ricaricamento può arrivare dopo la pagina
        mobileEnabled(enabled) {
            if (enabled && !this.entities.length) this.loadRegistry()
        },
    },

    created() {
        if (this.mobileEnabled) {
            this.loadRegistry()
        } else {
            this.loading = false
            this.endGlobalProgress()
        }
    },
}
</script>
